import { ImageIcon, Trash2 } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';

interface FileUploadProps {
  handleFile: (files: File[] | null) => void;
  label?: string;
  id?: string;
  element?: (props: {
    files: File[];
    removeFile: (index: number) => void;
    upload: () => void;
  }) => React.ReactNode;
  multiple?: boolean;
  accept: string;
  currentImageUrl?: string;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  handleFile,
  label,
  id,
  element,
  multiple,
  accept,
  currentImageUrl,
  disabled = false,
}) => {
  const inputFileId = id ?? `file-upload-${crypto.randomUUID()}`;
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    const updatedFiles = [...selectedFiles, ...newFiles];

    setSelectedFiles(updatedFiles);
    handleFile(updatedFiles);
  };

  const removeFile = (index: number) => {
    if (disabled) return;

    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);

    if (fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      newFiles.forEach((file) => {
        dataTransfer.items.add(file);
      });
      fileInputRef.current.files = dataTransfer.files;
    }
    handleFile(newFiles);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    const dropZone = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!relatedTarget || !dropZone.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    const validFiles = accept
      ? droppedFiles.filter((file) => {
          const acceptTypes = accept.split(',').map((type) => type.trim());
          return acceptTypes.some((acceptType) => {
            if (acceptType.startsWith('.') || !acceptType.includes('/')) {
              const extension = acceptType.startsWith('.') ? acceptType : `.${acceptType}`;
              return file.name.toLowerCase().endsWith(extension.toLowerCase());
            }
            if (acceptType.includes('/*')) {
              const mimeType = acceptType.split('/')[0];
              return file.type.startsWith(mimeType);
            }
            return file.type === acceptType;
          });
        })
      : droppedFiles;

    if (validFiles.length > 0) {
      const updatedFiles = multiple ? [...selectedFiles, ...validFiles] : validFiles;
      setSelectedFiles(updatedFiles);
      handleFile(updatedFiles);
    }
  };

  return (
    <div className="col-span-full">
      {element ? (
        <div>
          {element({
            files: selectedFiles,
            removeFile,
            upload: () => !disabled && fileInputRef.current?.click(),
          })}
          <input
            id={inputFileId}
            type="file"
            className="sr-only"
            multiple={multiple}
            accept={accept}
            onChange={onFileChange}
            ref={fileInputRef}
            disabled={disabled}
          />
        </div>
      ) : (
        <div>
          {label && (
            <label
              htmlFor="cover-photo"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              {label}
            </label>
          )}
          <button
            type="button"
            disabled={disabled}
            className={`mt-2 flex w-full gap-6 justify-center align-bottom rounded-lg border border-dashed px-6 py-10 transition-colors ${
              disabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : isDragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-900/25 hover:border-gray-400'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="text-center">
              <ImageIcon
                className={`mx-auto h-12 w-12 transition-colors ${
                  disabled ? 'text-gray-200' : isDragOver ? 'text-blue-400' : 'text-gray-300'
                }`}
                aria-hidden="true"
              />
              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                <span
                  className={`relative rounded-md bg-white font-semibold transition-colors ${
                    disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 cursor-pointer hover:text-blue-500'
                  }`}
                >
                  Selecione os arquivos
                  <input
                    id={inputFileId}
                    type="file"
                    className="sr-only"
                    multiple={multiple}
                    accept={accept}
                    onChange={onFileChange}
                    ref={fileInputRef}
                    disabled={disabled}
                  />
                </span>
                <p className={disabled ? 'pl-1 text-gray-400' : 'pl-1'}>ou arraste e solte</p>
              </div>
              {isDragOver && !disabled && (
                <p className="mt-2 text-sm text-blue-600 font-medium">Solte os arquivos aqui</p>
              )}
            </div>
          </button>

          {/* Current image preview */}
          {currentImageUrl && selectedFiles.length === 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Imagem atual:</p>
              <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-sm rounded-md px-4 py-2 w-fit">
                <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    src={currentImageUrl}
                    alt="Avatar atual"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-800 text-sm">Avatar atual</span>
                  <span className="text-xs text-gray-500">
                    Selecione uma nova imagem para alterar
                  </span>
                </div>
              </div>
            </div>
          )}

          <div>
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-900 mb-2">Arquivos inseridos:</p>
                <ul className="grid gap-3">
                  {selectedFiles.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex items-center justify-between bg-white border border-gray-200 shadow-sm rounded-md px-4 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                          {file.type.startsWith('image/') ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800 text-sm truncate max-w-[200px]">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        disabled={disabled}
                        className={`transition ${
                          disabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-500 hover:text-red-700'
                        }`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
